import { SxProps, styled, Box, Typography, Button, Stack, Alert } from "@mui/material";
import React, { ReactNode, useCallback } from "react";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

import { DropzoneOptions, useDropzone } from "react-dropzone";
import { fileService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export enum DropFileZoneContext {
  OrderBasketCheckout,
  OrderBasketDelivery,
}

export interface DropFileZoneProps {
  sx?: SxProps;
  name?: string;
  error?: boolean;
  dropZoneOptions?: DropzoneOptions;
  context: DropFileZoneContext;
  resourceId: number;
  onSuccess?: (files: File[], resourceId: number) => void;
}

enum UploadState {
  Ready,
  Uploading,
  Error,
  Completed,
}

export const bytesToKB = (bytes: number) => {
  return Math.round(bytes / 1024);
};

const DropFileZone: React.FC<DropFileZoneProps> = (props): ReactNode => {
  const { t } = useTranslation();
  const {
    error = false,
    dropZoneOptions = {},
    name = "drop-file-zone",
    context,
    resourceId,
    onSuccess,
    ...otherProps
  } = props;

  const [uploadState, setUploadState] = React.useState(UploadState.Ready);
  const [fileUpload, setFileUpload] = React.useState({
    fileName: "",
    progress: 0,
    size: 0,
  });
  const abortController = React.useRef(new AbortController());
  const enqueueCustomSnackbar = useCustomSnackbar();

  // On drop, let upload directly
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;

    setUploadState(UploadState.Uploading);

    switch (context) {
      case DropFileZoneContext.OrderBasketCheckout:
        fileService
          .AttachFileToItem(acceptedFiles[0], resourceId, abortController.current.signal, (progressEvent) => {
            const { loaded, total } = progressEvent;
            const percentValue = Math.floor((loaded * 100) / total);

            setFileUpload({ progress: percentValue, size: loaded, fileName: acceptedFiles[0].name });
          })
          .then((response) => {
            // on response, depending of status
            if (response.success) {
              setUploadState(UploadState.Completed);
              onSuccess && onSuccess(acceptedFiles, response.objectId ? response.objectId : -1);
            }
          })
          .catch((response) => {
            setUploadState(UploadState.Error);
          });
        break;
      case DropFileZoneContext.OrderBasketDelivery:
        fileService
          .AddDeliveryFileToBasketItem(
            acceptedFiles[0],
            resourceId,
            abortController.current.signal,
            (progressEvent) => {
              const { loaded, total } = progressEvent;
              const percentValue = Math.floor((loaded * 100) / total);

              setFileUpload({ progress: percentValue, size: loaded, fileName: acceptedFiles[0].name });
            }
          )
          .then((response) => {
            // on response, depending of status
            if (response.success) {
              setUploadState(UploadState.Completed);
              onSuccess && onSuccess(acceptedFiles, response.objectId ? response.objectId : -1);
            } else {
              setUploadState(UploadState.Error);
              enqueueCustomSnackbar(response.message || t("alert:error.oopsSomethingWentWrong"), "error");
            }
          })
          .catch((response) => {
            setUploadState(UploadState.Error);
          });
        break;
    }
  }, []);

  const { getRootProps, getInputProps, isDragReject, isDragActive } = useDropzone({
    onDrop,
    ...dropZoneOptions,
  });

  const onClickCancel = React.useCallback(() => {
    if (fileUpload.progress >= 100) return;

    // abort call and instantiate new controller (previous one is aborted, can't change it)
    abortController.current.abort();
    abortController.current = new AbortController();

    setUploadState(UploadState.Error);
  }, []);

  return (
    <UploadSlot isDragReject={isDragReject} error={error} active={isDragActive} {...otherProps}>
      {uploadState === UploadState.Uploading && (
        <>
          <Typography variant="subtitle2" component="span" fontWeight={700} mb={2}>
            {t("common:uploading")}
          </Typography>
          <ProgressBar progress={fileUpload.progress} />
          <Typography variant="body2" component="span" color="text.secondary" mt={1.5} mb={2}>
            {fileUpload.fileName} - {fileUpload.progress}% - {bytesToKB(fileUpload.size)} KB
          </Typography>
          <Button color="primary" variant="outlined" size="small" onClick={onClickCancel}>
            {t("common:cancel")}
          </Button>
        </>
      )}
      {uploadState === UploadState.Completed && (
        <>
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            {t("alert:success.uploadCompleted")}
          </Alert>
          <ProgressBar progress={fileUpload.progress} />
          <Typography variant="body2" component="span" color="text.secondary" mt={1.5} mb={2}>
            {fileUpload.fileName} - {fileUpload.progress}% - {bytesToKB(fileUpload.size)} KB
          </Typography>
        </>
      )}
      {uploadState === UploadState.Error && (
        <>
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {t("alert:error.uploadFailed")}
          </Alert>
          <ProgressBar progress={fileUpload.progress} />
          <Typography variant="body2" component="span" color="text.secondary" mt={1.5} mb={2}>
            {fileUpload.fileName} - {fileUpload.progress}% - {bytesToKB(fileUpload.size)} KB
          </Typography>
        </>
      )}
      {uploadState === UploadState.Ready && (
        <label htmlFor={name} {...getRootProps()}>
          <input name={name} id={name} {...getInputProps()} />
          <Stack alignItems="center">
            <Typography variant="subtitle2" component="span" fontWeight={600} onClick={(e) => e.stopPropagation()}>
              {t("common:dragYourFileHere")}
            </Typography>

            <Typography
              variant="body2"
              component="span"
              color="text.secondary"
              mt={1}
              onClick={(e) => e.stopPropagation()}
            >
              {t("component:body.uploadfileExplain")}
            </Typography>
            <Button
              color="primary"
              variant="contained"
              size="small"
              startIcon={<FileUploadOutlinedIcon />}
              sx={{ mt: 3 }}
            >
              {t("button:chooseFile")}
            </Button>
          </Stack>
        </label>
      )}
    </UploadSlot>
  );
};

export default DropFileZone;

const UploadSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sx" && prop !== "error" && prop !== "active" && prop !== "isDragReject",
})<{
  error: boolean;
  isDragReject: boolean;
  active: boolean;
}>(({ theme, error, isDragReject, active }) => ({
  position: "relative",
  minHeight: 164,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  border: `1px dashed ${theme.palette.backgroundAlpha.borderFocus}`,
  borderRadius: 12,
  padding: `${theme.spacing(3.5)} ${theme.spacing(4)}`,
  transition: "all .3s",

  label: {
    cursor: "pointer",
  },

  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
  },
  ...(active && {
    borderColor: theme.palette.backgroundAlpha.borderFocus,
    background: theme.palette.backgroundAlpha.borderFocus,
  }),
  ...((isDragReject || error) && {
    borderColor: theme.palette.error.main,
  }),
  ...(isDragReject && {
    background: theme.palette.error.light,
  }),

  ".error-text": {
    position: "absolute",
    bottom: theme.spacing(1),
    opacity: isDragReject ? 1 : 0,
    transition: "opacity .3s",
    pointerEvents: "none",
  },
}));

export const ProgressBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sx" && prop !== "progress",
})<{
  progress: number;
}>(({ theme, progress }) => ({
  position: "relative",
  width: "100%",
  height: 4,
  borderRadius: 2,
  background: theme.palette.backgroundAlpha.main,
  overflow: "hidden",

  "&:after": {
    content: '""',
    position: "absolute",
    left: 0,
    transform: `scaleX(${progress / 100})`,
    width: "100%",
    height: 4,
    transformOrigin: "left",
    background: theme.palette.primary.main,
  },
}));
